import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Form, Dropdown, DropdownButton, FormControl, Spinner } from 'react-bootstrap';
import './CompanySelect.css';

const CompanySelect = ({ 
  label = "Selecionar Empresa", 
  options = [], 
  selectedValue, 
  onSelectionChange, 
  disabled = false,
  placeholder = "Buscar empresa..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const bodyHasDarkClass = document.body.classList.contains('dark-theme');
    const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
    
    if (savedTheme) {
      return savedTheme;
    }
    
    return bodyHasDarkClass || htmlHasDarkClass ? 'dark' : 'light';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const bodyHasDarkClass = document.body.classList.contains('dark-theme');
      const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
      const savedTheme = localStorage.getItem('theme');
      
      let detectedTheme = 'light';
      
      if (savedTheme) {
        detectedTheme = savedTheme;
      } else if (bodyHasDarkClass || htmlHasDarkClass) {
        detectedTheme = 'dark';
      }
      
      setTheme(detectedTheme);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  // Memoiza as opções filtradas para evitar cálculos desnecessários
  const filteredOptions = useMemo(() => {
    return options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Encontra a opção selecionada
  const selectedOption = useMemo(() => {
    return options.find(option => option._id === selectedValue);
  }, [options, selectedValue]);

  // Função de seleção única
  const handleSelect = useCallback((selectedOption) => {
    if (onSelectionChange) {
      onSelectionChange(selectedOption._id, selectedOption);
    }
    setShowDropdown(false); // Fecha o dropdown após seleção
  }, [onSelectionChange]);

  // Função para limpar a seleção
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(null, null);
    }
  }, [onSelectionChange]);

  // Função para limpar via dropdown
  const handleClearFromDropdown = useCallback((e) => {
    e.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(null, null);
    }
    setShowDropdown(false);
  }, [onSelectionChange]);

  // Debounce manual para otimizar a pesquisa e evitar execuções frequentes
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
    }, 300);
  }, []);

  // Simula o carregamento inicial de dados ao abrir o dropdown
  const handleToggle = useCallback((isOpen) => {
    setShowDropdown(isOpen);
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false); // Simula a conclusão do carregamento
      }, 100);
    }
  }, []);

  return (
    <div className="company-select-container mt-1 mb-1">
      <Form.Group>
        <Form.Label className="mb-2">{label}</Form.Label>
        <div style={{ position: 'relative' }}>
          <DropdownButton
            id="dropdown-company-select"
            title={
              selectedOption ? selectedOption.name : label
            }
            variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
            show={showDropdown}
            onToggle={handleToggle}
            disabled={disabled}
            className={`w-100 ${selectedOption ? 'has-clear-button' : ''}`}
          >
            {/* Opção para limpar seleção */}
            {selectedOption && (
              <div className="company-select-clear-option">
                <Dropdown.Item
                  onClick={handleClearFromDropdown}
                  className="text-center"
                >
                  <i className="fas fa-times me-2"></i>
                  Remover seleção
                </Dropdown.Item>
              </div>
            )}

            <FormControl
              className="border border-secondary rounded"
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearch}
              style={{ 
                marginTop: '0.5rem', 
                marginLeft: '0.5rem', 
                marginRight: '0.5rem', 
                marginBottom: 0, 
                width: '90%' 
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <label 
              className="company-select-total-label"
              style={{ 
                marginLeft: '1rem', 
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Total: <strong>{filteredOptions.length}</strong>
            </label>
            <hr className="m-0" />
            <div className="company-select-dropdown-list">
              {loading ? (
                <div className="company-select-loading d-flex justify-content-center align-items-center pt-3 pb-2">
                  <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} size="sm" />
                  <span className="ms-2">Carregando...</span>
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <Dropdown.Item
                    key={index}
                    active={selectedValue === option._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(option);
                    }}
                    className="company-select-dropdown-item"
                    style={{
                      cursor: 'pointer',
                      fontSize: '14px',
                      backgroundColor: selectedValue === option._id 
                        ? (theme === 'dark' ? '#0d6efd' : '#0d6efd')
                        : 'transparent',
                      color: selectedValue === option._id 
                        ? '#fff' 
                        : (theme === 'dark' ? '#fff' : '#000')
                    }}
                    onMouseEnter={(e) => {
                      if (selectedValue !== option._id) {
                        e.currentTarget.style.backgroundColor = theme === 'dark' ? '#495057' : '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedValue !== option._id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {option.name}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled className="text-center">
                  Nenhuma empresa encontrada
                </Dropdown.Item>
              )}
            </div>
          </DropdownButton>
          
          {/* Botão de limpeza externo */}
          {selectedOption && !disabled && (
            <button
              type="button"
              className="company-select-clear-button"
              onClick={handleClear}
              title="Limpar seleção"
            >
              ✕
            </button>
          )}
        </div>
      </Form.Group>
    </div>
  );
};

export default CompanySelect;
